import { useState } from "react";
import { X, Copy, ExternalLink, Check } from "lucide-react";
import {
  COLUMNS, COLUMN_LABELS, SOURCE_LABELS, PRIORITY_LABELS, PRIORITIES, TYPE_LABELS,
  ACTIVITY_TYPE_LABELS, safeExternalUrl, fmtDateTime,
} from "./growthConstants";

// Drawer lateral de detalle/edición — sección 13 del spec: info principal, contexto (summary/
// whyRelevant), respuesta recomendada con "Copiar", respuesta publicada editable, próxima
// acción + seguimiento, estado comercial (switches), notas, timeline de actividad.
export default function GrowthDrawer({ card, onClose, onPatch, onMove, busy }) {
  const [copied, setCopied] = useState(false);
  const [publishedReply, setPublishedReply] = useState(card.publishedReply || "");
  const [nextAction, setNextAction] = useState(card.nextAction || "");
  const [followUpAt, setFollowUpAt] = useState(card.followUpAt ? card.followUpAt.slice(0, 16) : "");
  const [notes, setNotes] = useState(card.notes || "");

  const url = safeExternalUrl(card.sourceUrl);
  const copyReply = () => {
    navigator.clipboard.writeText(card.recommendedReply || "").then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const toggle = (field) => onPatch({ [field]: !card[field] });

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="growth-drawer"
        className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-white shadow-2xl">
        <div className="sticky top-0 flex items-start justify-between gap-3 border-b border-slate-100 bg-white p-4">
          <div className="min-w-0">
            <h2 className="truncate text-base font-bold text-slate-900">{card.title}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
              <span>{SOURCE_LABELS[card.source] || card.source}</span>
              {card.sourceUser && <span>· @{card.sourceUser}</span>}
              <span>· {TYPE_LABELS[card.type] || card.type}</span>
            </div>
          </div>
          <button onClick={onClose} className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>

        <div className="flex-1 space-y-5 p-4">
          {url && (
            <a href={url} target="_blank" rel="noopener noreferrer"
              className="flex w-fit items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50">
              Abrir fuente <ExternalLink size={14} />
            </a>
          )}

          <div className="grid grid-cols-2 gap-2">
            <select value={card.column} onChange={(e) => onMove(e.target.value)} data-testid="growth-drawer-column"
              className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm focus:border-red-400 focus:outline-none">
              {COLUMNS.map((c) => <option key={c} value={c}>{COLUMN_LABELS[c]}</option>)}
            </select>
            <select value={card.priority} onChange={(e) => onPatch({ priority: e.target.value })}
              className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm focus:border-red-400 focus:outline-none">
              {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
            </select>
          </div>

          {(card.summary || card.whyRelevant) && (
            <div className="space-y-2 rounded-lg bg-slate-50 p-3 text-sm">
              {card.summary && <p className="text-slate-700">{card.summary}</p>}
              {card.whyRelevant && <p className="text-slate-500">{card.whyRelevant}</p>}
            </div>
          )}

          {card.recommendedReply && (
            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Respuesta recomendada</span>
                <button onClick={copyReply} data-testid="growth-copy-reply"
                  className="flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700">
                  {copied ? <><Check size={12} /> Copiado</> : <><Copy size={12} /> Copiar</>}
                </button>
              </div>
              <p className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-white p-2.5 text-sm text-slate-700">{card.recommendedReply}</p>
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">Respuesta publicada</label>
            <textarea value={publishedReply} onChange={(e) => setPublishedReply(e.target.value)}
              onBlur={() => onPatch({ publishedReply })} rows={3}
              placeholder="Lo que realmente publicaste/enviaste"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-red-400 focus:outline-none" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">Próxima acción</label>
              <input value={nextAction} onChange={(e) => setNextAction(e.target.value)}
                onBlur={() => onPatch({ nextAction })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-red-400 focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">Seguimiento</label>
              <input type="datetime-local" value={followUpAt} onChange={(e) => setFollowUpAt(e.target.value)}
                onBlur={() => onPatch({ followUpAt: followUpAt ? new Date(followUpAt).toISOString() : null })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-red-400 focus:outline-none" />
            </div>
          </div>

          <div>
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400">Estado comercial</span>
            <div className="flex flex-wrap gap-1.5">
              {card.registered && (
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">✓ Registrado</span>
              )}
              {["responded", "activated", "customer"].map((field) => {
                const labels = { responded: "💬 Respondió", activated: "⚡ Activado", customer: "€ Cliente" };
                const active = !!card[field];
                return (
                  <button key={field} onClick={() => toggle(field)} data-testid={`growth-toggle-${field}`}
                    className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${active ? "border-red-300 bg-red-50 text-red-700" : "border-slate-200 text-slate-400 hover:bg-slate-50"}`}>
                    {labels[field]}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">Notas</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              onBlur={() => onPatch({ notes })} rows={3}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-red-400 focus:outline-none" />
          </div>

          {card.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {card.tags.map((t) => (
                <span key={t} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">{t}</span>
              ))}
            </div>
          )}

          <div>
            <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-400">Historial</span>
            <div className="space-y-2">
              {(card.activities || []).length === 0 && <p className="text-xs text-slate-400">Sin actividad registrada.</p>}
              {(card.activities || []).map((a) => (
                <div key={a.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-slate-700">{ACTIVITY_TYPE_LABELS[a.type] || a.type}</span>
                    <span className="shrink-0 text-slate-400">{fmtDateTime(a.createdAt)}</span>
                  </div>
                  {a.text && <p className="mt-0.5 text-slate-500">{a.text}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
